import { FC, PropsWithChildren, useEffect, useState } from "react";

import {
    PortalConfiguration,
    PortalConfigurationOptions,
    SiteConfigurationProvider,
} from "@dit/ui-configurations/configurations";
import { GlobalConfigDefinition } from "./core/globalConfig";
import { IsConfigModeLocalGlobally, IsDevelopment } from "./core/configuration";
import { AppNotLoaded } from "./components/pages/AppNotLoaded";
import { client as userExperienceClient } from "@app/services/openapi/userexperience/client/client.gen";

type LoadConfigurationProps = {
    baseConfig: GlobalConfigDefinition;
};

const isLocal = IsDevelopment && IsConfigModeLocalGlobally;
const configProvider = isLocal ? SiteConfigurationProvider.local : SiteConfigurationProvider.remote;

// runs once the merged config is available (local mode: baseConfig; remote mode: PortalConfiguration onFetchSuccess).
function onConfigLoaded(config: GlobalConfigDefinition) {
    const linkFavicon = document.getElementById("favicon") as HTMLLinkElement;
    if (linkFavicon) {
        linkFavicon.href = config.faviconUrl || "./favicon.png";
    }

    userExperienceClient.setConfig({ baseUrl: config?.services?.UserExperienceAPI?.uri });
}

export const SyncConfiguration: FC<PropsWithChildren<LoadConfigurationProps>> = ({ baseConfig, children }) => {
    const [hasError, setHasError] = useState(false);
    const [errorStatusCode, setErrorStatusCode] = useState("");
    const [isLoadedLocal, setIsLoadedLocal] = useState<boolean>(!isLocal);

    // Need an effect if local because PortalConfiguration doesn't call onFetchSuccess when using the local provider
    useEffect(() => {
        if (isLocal) {
            queueMicrotask(() => {
                onConfigLoaded(baseConfig);
                setIsLoadedLocal(true);
            });
        }
    }, [baseConfig]);

    const options: PortalConfigurationOptions<GlobalConfigDefinition> = {
        configurationProvider: configProvider,
        remoteConfigurationUrl: new URL(
            `${baseConfig.services.ConfigurationAPI.uri}/SiteConfigurationValues`,
            window.location.origin
        ),
        loader: null,
        onFetchFailure: (e: any) => {
            setHasError(true);
            setErrorStatusCode(e.message);
        },
        onFetchSuccess: (config: GlobalConfigDefinition) => {
            onConfigLoaded(config);
        },
    };

    if (hasError) {
        return <AppNotLoaded errorStatusCode={errorStatusCode} />;
    }

    if (!isLoadedLocal) {
        return null;
    }

    return (
        <PortalConfiguration options={options} defaultConfiguration={baseConfig}>
            {children}
        </PortalConfiguration>
    );
};
